/**
 * Google Analytics 4 tracking utilities
 *
 * Implementation Notes:
 * - GA4 measurement ID should be configured via environment variable
 * - Only tracks events in production to avoid polluting analytics data
 * - Provides type-safe event tracking methods
 *
 * Usage:
 * ```typescript
 * import { trackEvent, trackPageView } from '@/lib/analytics';
 *
 * // Track custom event
 * trackEvent('search', 'venues', 'keyword: gaming');
 *
 * // Track page view (usually called in router)
 * trackPageView('/venues');
 * ```
 */

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js',
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Get GA4 measurement ID from environment
 * Format: G-XXXXXXXXXX
 */
const getGAMeasurementId = (): string | null => {
  // In production, this should come from environment variable
  // For now, return null to prevent tracking in development
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  return measurementId || null;
};

/**
 * Check if analytics is enabled
 * Only enable in production with valid measurement ID
 */
const isAnalyticsEnabled = (): boolean => {
  const measurementId = getGAMeasurementId();
  const isProduction = import.meta.env.PROD;
  return isProduction && !!measurementId && typeof window.gtag === 'function';
};

/**
 * Track a custom event
 *
 * @param action - The action being tracked (e.g., 'search', 'click', 'submit')
 * @param category - The category of the event (e.g., 'venues', 'bookings')
 * @param label - Optional label for additional context
 * @param value - Optional numeric value
 *
 * @example
 * trackEvent('search', 'venues', 'keyword: gaming');
 * trackEvent('click', 'cta_button', 'book_now', 1);
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!isAnalyticsEnabled()) {
    // In development, log to console for debugging
    if (import.meta.env.DEV) {
      console.warn('[Analytics]', { action, category, label, value });
    }
    return;
  }

  window.gtag?.('event', action, {
    event_category: category,
    event_label: label,
    value,
  });
};

/**
 * Track a page view
 * Should be called on route changes
 *
 * @param path - The page path (e.g., '/venues', '/bookings/123')
 * @param title - Optional page title
 *
 * @example
 * trackPageView('/venues');
 * trackPageView('/bookings/123', 'Booking Details');
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Page view:', { path, title });
    }
    return;
  }

  const measurementId = getGAMeasurementId();
  if (!measurementId) return;

  window.gtag?.('config', measurementId, {
    page_path: path,
    page_title: title,
  });
};

/**
 * Venue-related event tracking
 */
export const trackVenueEvent = {
  search: (query: string, resultCount: number) =>
    trackEvent('search', 'venues', `query: ${query}`, resultCount),
  view: (venueId: string, venueName: string) =>
    trackEvent('view_item', 'venues', `${venueName} (${venueId})`),
  filter: (filterType: string, filterValue: string) =>
    trackEvent('filter', 'venues', `${filterType}: ${filterValue}`),
  share: (venueId: string, method: string) =>
    trackEvent('share', 'venues', `${venueId} via ${method}`),
};

/**
 * Booking flow event tracking
 */
export const trackBookingEvent = {
  start: (venueId: string, venueName: string) =>
    trackEvent('begin_checkout', 'bookings', `${venueName} (${venueId})`),
  step: (step: number, label: string) => trackEvent('checkout_progress', 'bookings', label, step),
  complete: (bookingId: string, amount: number) =>
    trackEvent('purchase', 'bookings', bookingId, amount),
  cancel: (bookingId: string, reason?: string) =>
    trackEvent('cancel_booking', 'bookings', reason ? `${bookingId}: ${reason}` : bookingId),
};

/**
 * Payment event tracking
 */
export const trackPaymentEvent = {
  selectMethod: (method: string) => trackEvent('add_payment_info', 'payments', method),
  success: (transactionId: string, amount: number) =>
    trackEvent('payment_success', 'payments', transactionId, amount),
  failure: (errorCode: string, errorMessage: string) =>
    trackEvent('payment_failure', 'payments', `${errorCode}: ${errorMessage}`),
};

/**
 * Auth event tracking
 */
export const trackAuthEvent = {
  signup: (method: string) => trackEvent('sign_up', 'auth', method),
  login: (method: string) => trackEvent('login', 'auth', method),
  logout: () => trackEvent('logout', 'auth'),
};

/**
 * Vendor onboarding event tracking
 */
export const trackOnboardingEvent = {
  welcomeDismissed: () => trackEvent('onboarding_welcome_dismissed', 'onboarding'),
  checklistDismissed: () => trackEvent('onboarding_checklist_dismissed', 'onboarding'),
  stepCompleted: (step: string) => trackEvent('onboarding_step_completed', 'onboarding', step),
  completed: (timeSinceStart: number) =>
    trackEvent('onboarding_completed', 'onboarding', undefined, timeSinceStart),
};

/**
 * Track error events for monitoring
 */
export const trackError = (errorType: string, errorMessage: string, fatal = false): void => {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Error:', { errorType, errorMessage, fatal });
    }
    return;
  }

  window.gtag?.('event', 'exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal,
  });
};
